import { yupResolver } from '@hookform/resolvers/yup';
import {
	Button,
	FormControl,
	FormHelperText,
	InputAdornment,
	TextField,
	Typography,
} from '@mui/material';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from 'store/store';
import { changeUserPassword } from 'store/user-service/asyncActions';
import { getChangePasswordError, getIsLoading, getStripeInstance } from 'store/user-service/selector';
import { logout, setStripeInstance } from 'store/user-service/userSlice';
import { resetTicketsStore } from 'store/ticket-service/ticketSlice';
import { resetTicketShareStore } from 'store/ticket-share-service/ticketShareSlice';

import { NamespacesList } from 'core/enums/common';
import { errorNotify, successNotify } from 'core/helpers/toasts';
import { userChangePassword } from 'core/helpers/yupSchemas';
import { ROUTES } from 'core/constants/routes';
import usePaymentStream from 'core/hooks/usePaymentStream';

import { SubmitButton } from 'components/Profile/styled';
import { SharedModal } from 'components/shared/Modal';

import { ReactComponent as EyeSlash } from 'assets/icons/eye-slash.svg';
import { ReactComponent as Lock } from 'assets/icons/lock.svg';
import { ReactComponent as Eye } from 'assets/icons/eye.svg';
import * as Styled from './styled';

interface ChangePasswordModalProps {
	open: boolean;
	onClose: () => void;
}

export const ChangePasswordModal: FC<ChangePasswordModalProps> = ({ open, onClose }) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const { t: modalTranslate } = useTranslation(NamespacesList['modals']);
	const { t: changePasswordTranslate } = useTranslation(NamespacesList['changePassword']);

	const [showOldPassword, setShowOldPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

	const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '{}') : '';

	const isLoading = useSelector(getIsLoading);
	const changePasswordError = useSelector(getChangePasswordError);
	const stripeInstance = useSelector(getStripeInstance);
	const { closeConnection } = usePaymentStream();
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const {
		handleSubmit,
		register,
		formState: { errors, isDirty },
		reset,
		setError,
	} = useForm({
		resolver: yupResolver(userChangePassword()),
	});

	const handleClose = () => {
		onClose();
		reset();
		setShowOldPassword(false);
		setShowNewPassword(false);
		setShowConfirmNewPassword(false);
	};

	const handleClickShowOldPassword = () => {
		setShowOldPassword(!showOldPassword);
	};

	const handleClickShowNewPassword = () => {
		setShowNewPassword(!showNewPassword);
	};

	const handleClickShowConfirmNewPassword = () => {
		setShowConfirmNewPassword(!showConfirmNewPassword);
	};

	const handleChangePassword = async (data: {
		oldPassword: string;
		newPassword: string;
		confirmNewPassword: string;
	}) => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { confirmNewPassword, ...transferredData } = data;
		try {
			await dispatch(
				changeUserPassword({
					id: user.id,
					data: transferredData,
					function: handleClose,
				})
			).unwrap();
			// Show success message
			successNotify(
				commonTranslate('errors.success'),
				commonTranslate('errors.successMessage')
			);
			
			// Déconnexion après changement de mot de passe réussi
			const handleLogout = () => {
				dispatch(logout());
				dispatch(resetTicketsStore());
				dispatch(resetTicketShareStore());
				Object.keys(localStorage).forEach((key) => {
					if (key !== 'i18nextLng' && key !== 'paymentModalOpenings') {
						localStorage.removeItem(key);
					}
				});
				if (stripeInstance) {
					closeConnection(stripeInstance.connection);
					dispatch(setStripeInstance(null));
				}
				navigate(ROUTES.LOGIN);
			};
			
			// Appeler la déconnexion après un court délai pour permettre à l'utilisateur de voir le message de succès
			setTimeout(() => {
				handleLogout();
			}, 1000);
		} catch (error: any) {
			// Error is already stored in the reducer via rejected case
			// Extract error message from the rejected value (which is error.data from async action)
			// The error from rejectWithValue(error.data) will be directly error.data
			let errorMessage: string;
			
			if (typeof error === 'string') {
				// If error is directly a string (e.g., "The old password is incorrect")
				errorMessage = error;
			} else if (error?.message) {
				// If error has a message property
				errorMessage = error.message;
			} else if (error?.response?.data) {
				// If error has response.data
				errorMessage = typeof error.response.data === 'string' 
					? error.response.data 
					: error.response.data?.message || error.response.data;
			} else if (error?.data) {
				// If error has data property
				errorMessage = typeof error.data === 'string' 
					? error.data 
					: error.data?.message || JSON.stringify(error.data);
			} else {
				// Fallback to default error message
				errorMessage = commonTranslate('errors.somethingWentWrongDescription');
			}
			
			// Display error in snackbar
			errorNotify(
				commonTranslate('errors.error'),
				errorMessage
			);
			
			// Set error on the oldPassword field
			setError('oldPassword', {
				type: 'server',
				message: errorMessage,
			});
		}
	};

	return (
		<SharedModal isOpen={open} handleClose={handleClose}>
			<Styled.ChangePasswordModalContent>
				<Lock />
				<Styled.Title>{modalTranslate('changePasswordModal.title')}</Styled.Title>
				<form onSubmit={handleSubmit(handleChangePassword)} style={{ width: '100%' }}>
					<Styled.Form className="rtl-able">
						<FormControl fullWidth error={!!errors.oldPassword || !!changePasswordError}>
							<Typography sx={{ fontSize: '12px' }}>
								{changePasswordTranslate('text.oldPassword')}
							</Typography>
							<TextField
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<Button
												disableRipple
												sx={{
													minWidth: 0,
													'&:hover': {
														backgroundColor: 'rgba(0, 0, 0, 0.1)',
													},
												}}
												onClick={handleClickShowOldPassword}
											>
												
												{showOldPassword ? <Eye /> : <EyeSlash />}
											</Button>
										</InputAdornment>
									),
								}}
								inputProps={{
									style: {
										padding: '10px 12px',
									},
								}}
								sx={{
									height: '44px',
									'& .MuiOutlinedInput-root': {
										borderRadius: '8px',
									},
								}}
								autoComplete="password"
								type={showOldPassword ? 'text' : 'password'}
								placeholder={changePasswordTranslate('placeholders.password') as string}
								{...register('oldPassword')}
							/>
							<FormHelperText
								sx={{
									fontSize: '12px',
									textAlign: 'right',
									marginRight: '12px',
									marginTop: '0',
								}}
							>
								{errors.oldPassword?.message || changePasswordError}
							</FormHelperText>
						</FormControl>
						<FormControl fullWidth error={!!errors.newPassword}>
							<Typography sx={{ fontSize: '12px' }}>
								{changePasswordTranslate('text.newPassword')}
							</Typography>
							<TextField
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<Button
												disableRipple
												sx={{
													minWidth: 0,
													'&:hover': {
														backgroundColor: 'rgba(0, 0, 0, 0.1)',
													},
												}}
												onClick={handleClickShowNewPassword}
											>
												
												{showNewPassword ? <Eye /> : <EyeSlash />}
											</Button>
										</InputAdornment>
									),
								}}
								inputProps={{
									style: {
										padding: '10px 12px',
									},
								}}
								sx={{
									height: '44px',
									'& .MuiOutlinedInput-root': {
										borderRadius: '8px',
									},
								}}
								autoComplete="password"
								type={showNewPassword ? 'text' : 'password'}
								placeholder={changePasswordTranslate('placeholders.password') as string}
								{...register('newPassword')}
							/>
							<FormHelperText
								sx={{
									fontSize: '12px',
									textAlign: 'right',
									marginRight: '12px',
									marginTop: '0',
								}}
							>
								{errors.newPassword?.message}
							</FormHelperText>
						</FormControl>
						<FormControl fullWidth error={!!errors.confirmNewPassword}>
							<Typography sx={{ fontSize: '12px' }}>
								{changePasswordTranslate('text.confirmNewPassword')}
							</Typography>
							<TextField
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<Button
												disableRipple
												sx={{
													minWidth: 0,
													'&:hover': {
														backgroundColor: 'rgba(0, 0, 0, 0.1)',
													},
												}}
												onClick={handleClickShowConfirmNewPassword}
											>
												
												{showConfirmNewPassword ? <Eye /> : <EyeSlash />}
											</Button>
										</InputAdornment>
									),
								}}
								inputProps={{
									style: {
										padding: '10px 12px',
									},
								}}
								sx={{
									height: '44px',
									'& .MuiOutlinedInput-root': {
										borderRadius: '8px',
									},
								}}
								autoComplete="password"
								type={showConfirmNewPassword ? 'text' : 'password'}
								placeholder={changePasswordTranslate('placeholders.password') as string}
								{...register('confirmNewPassword')}
							/>
							<FormHelperText
								sx={{
									fontSize: '12px',
									textAlign: 'right',
									marginRight: '12px',
									marginTop: '0',
								}}
							>
								{errors.confirmNewPassword?.message}
							</FormHelperText>
						</FormControl>
						{isLoading ? (
							<SubmitButton variant="contained" color="primary" disabled={isLoading}>
								<Oval
									height={24}
									width={24}
									color="#fff"
									wrapperStyle={{}}
									wrapperClass=""
									visible={true}
									ariaLabel="oval-loading"
									secondaryColor="#91B6FF"
									strokeWidth={5}
									strokeWidthSecondary={5}
								/>
							</SubmitButton>
						) : (
							<SubmitButton
								type="submit"
								variant="contained"
								color="primary"
								// disabled
								disabled={!isDirty}
							>
								{modalTranslate('changePasswordModal.title')}
							</SubmitButton>
						)}
					</Styled.Form>
				</form>
			</Styled.ChangePasswordModalContent>
		</SharedModal>
	);
};
